import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { apiUrl } from '@app/app.constants';
import { File as PortalFile } from '@app/core/interfaces/download/file';
import { FileUpdate } from '@app/core/interfaces/download/file-update';
import { FileRepairError } from '@app/core/interfaces/file-repair-error';
import { SystemFiles } from '@app/core/interfaces/portal/system-files';
import { UserLevel } from '@app/core/models/user-level';
import { Upload, UploadService } from '@app/shared/services/upload.service';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FileRepository {
  constructor(
    private http: HttpClient,
    private uploadService: UploadService,
    private utils: Utils
  ) {}

  create({ content, downloadId, file }: CreateFileOptions): Observable<Upload<PortalFile>> {
    const formData = new FormData();

    formData.append('file', JSON.stringify(file));
    if (content != null) {
      formData.append('content', content, content.name);
    }

    return this.utils.routeBasedApiUrl(`/downloads/${downloadId}/files`).pipe(
      mergeMap((url) =>
        this.uploadService.post<PortalFile>({
          formData,
          url,
        })
      ),
      wrapError('files-repository:create')
    );
  }

  delete(id: number): Observable<void> {
    return this.utils.routeBasedApiUrl(`/files/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('files-repository:delete')
    );
  }

  deleteSystemFile(path: string): Observable<void> {
    return this.utils.routeBasedApiUrl(`/files`).pipe(
      mergeMap((url) =>
        this.http.delete<void>(url, {
          params: {
            path,
          },
        })
      ),
      wrapError('file-repository:delete-system-file')
    );
  }

  downloadApiUrl(id: number): string {
    return apiUrl(`/files/download/${id}`);
  }

  listSystemFiles(userLevel?: UserLevel): Observable<SystemFiles> {
    return this.utils.routeBasedApiUrl('/files', userLevel).pipe(
      mergeMap((url) => this.http.get<SystemFiles>(url)),
      wrapError('files-repository:list-system-files')
    );
  }

  repair(userLevel?: UserLevel): Observable<FileRepairError[]> {
    return this.utils.routeBasedApiUrl('/files/repair', userLevel).pipe(
      mergeMap((url) => this.http.post<FileRepairError[]>(url, undefined)),
      wrapError('file-repository:repair')
    );
  }

  update({ content, file, id }: FileUpdateOptions): Observable<Upload<PortalFile>> {
    const formData = new FormData();

    formData.append('file', JSON.stringify(file));

    if (content != null) {
      formData.append('content', content, content.name);
    }

    return this.utils.routeBasedApiUrl(`/files/${id}`).pipe(
      mergeMap((url) =>
        this.uploadService.put<PortalFile>({
          formData,
          url,
        })
      ),
      wrapError('files-repository:update')
    );
  }
}

export interface CreateFileOptions {
  content?: File;
  downloadId: number;
  file: FileUpdate;
}

export interface FileUpdateOptions {
  content?: File;
  file: FileUpdate;
  id: number;
}
